import { Link, NavLink, useNavigate } from "react-router-dom";
import { useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { NAV } from "@/constants/testIds";
import { Menu, X, LogOut } from "lucide-react";

const linkBase =
  "text-[13px] font-medium tracking-wide transition-colors duration-200";
const linkInactive = "text-zinc-500 hover:text-zinc-900";
const linkActive = "text-zinc-900 border-b border-zinc-900 pb-1";

export default function Navbar() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  if (!user) return null;

  const remaining = user.generation_remaining ?? 0;
  const limit = user.generation_limit ?? 100;

  const links = [
    { to: "/dashboard", label: "Dashboard", testid: NAV.dashboard },
    { to: "/create", label: "Photo Studio", testid: NAV.create },
    { to: "/content", label: "Content Studio", testid: NAV.content },
    { to: "/history", label: "History", testid: NAV.history },
    { to: "/profile", label: "Profile", testid: NAV.profile },
  ];
  if (user.is_admin) links.push({ to: "/admin", label: "Admin", testid: NAV.admin });

  const onLogout = async () => {
    await logout();
    navigate("/login", { replace: true });
  };

  return (
    <header className="sticky top-0 z-50 backdrop-blur-xl bg-[#FBFBF9]/85 border-b border-zinc-200/70">
      <div className="max-w-7xl mx-auto px-6 lg:px-10 h-16 flex items-center justify-between">
        <Link to="/dashboard" className="flex items-center gap-3">
          <div className="h-8 w-8 bg-zinc-950 flex items-center justify-center">
            <span className="font-serif text-[#FBFBF9] text-lg leading-none">Æ</span>
          </div>
          <div className="flex flex-col leading-none">
            <span className="font-serif text-[15px] tracking-tight text-zinc-950">AI Photo & Content Studio</span>
          </div>
        </Link>

        <nav className="hidden md:flex items-center gap-8">
          {links.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              data-testid={l.testid}
              className={({ isActive }) => `${linkBase} ${isActive ? linkActive : linkInactive}`}
            >
              {l.label}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          {/* generations badge hidden per request */}

          <button
            data-testid={NAV.userButton}
            onClick={() => navigate("/profile")}
            className="flex items-center gap-2"
          >
            {user.picture ? (
              <img src={user.picture} alt="" className="h-8 w-8 rounded-full object-cover" />
            ) : (
              <div className="h-8 w-8 rounded-full bg-zinc-200 flex items-center justify-center text-[11px] font-medium">
                {(user.name || user.email || "?").charAt(0).toUpperCase()}
              </div>
            )}
          </button>

          <button
            data-testid={NAV.logoutButton}
            onClick={onLogout}
            className="hidden md:inline-flex items-center gap-1.5 text-zinc-500 hover:text-zinc-900 text-[12px]"
          >
            <LogOut className="h-3.5 w-3.5" /> Sign out
          </button>

          <button
            data-testid={NAV.mobileMenuToggle}
            className="md:hidden"
            onClick={() => setOpen((v) => !v)}
          >
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </div>

      {open && (
        <div className="md:hidden border-t border-zinc-200 bg-[#FBFBF9]">
          <div className="px-6 py-4 flex flex-col gap-3">
            {links.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                onClick={() => setOpen(false)}
                data-testid={`${l.testid}-mobile`}
                className={({ isActive }) =>
                  `text-sm ${isActive ? "text-zinc-950 font-medium" : "text-zinc-600"}`
                }
              >
                {l.label}
              </NavLink>
            ))}
            <button onClick={onLogout} className="text-left text-sm text-zinc-600">
              Sign out
            </button>
          </div>
        </div>
      )}
    </header>
  );
}
